import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useGetFieldForceDetails,
  useUpdateFieldForce,
  useGetSkills,
  useGetCertificates,
  useRates,
  useVerifyEmail,
} from "../../hooks";
import { Button } from "@shared/ui/button";
import { Upload, X, ArrowLeft, Info, AlertTriangle } from "lucide-react";
import { toast } from "@shared/ui/use-toast";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { Card } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { useAreaList, useSubAreaList } from "@shared/api/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { Separator } from "@shared/ui/separator";

interface FileUploadComponentProps {
  file: File | null;
  filePreview: string | null;
  isLoading: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
}

const FileUploadComponent: React.FC<FileUploadComponentProps> = React.memo(
  ({ file, filePreview, isLoading, onFileChange, onRemoveFile }) => {
    return (
      <div className="w-full bg-[#f0f4ff] p-6 rounded-lg border border-blue-100 mb-6 flex gap-6 items-start">
        <div className="h-32 w-32 bg-white rounded-xl flex items-center justify-center border border-gray-200 relative shadow-sm shrink-0">
          {filePreview ? (
            <img
              src={filePreview}
              alt="Preview"
              className="h-full w-full object-cover rounded-xl"
            />
          ) : (
            <div className="text-center">
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 text-blue-600">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="7"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="text-xs text-gray-400 font-medium">
                No photo
              </span>
            </div>
          )}
          {/* <div
            className="absolute -bottom-2 -right-2 bg-blue-600 h-8 w-8 rounded-full flex items-center justify-center border-2 border-white cursor-pointer"
            onClick={() =>
              !isLoading && document.getElementById("file-upload")?.click()
            }
          >
            <Upload className="h-4 w-4 text-white" />
          </div> */}
        </div>

        <div className="flex-1 space-y-4">
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-1">
              Profile Photo
            </h3>
            <p className="text-sm text-gray-500">
              Upload a clear, professional photo for employee identification
            </p>
          </div>

          <div className="flex items-center gap-2 w-full">
            <div
              className={`border rounded-md px-4 py-2 bg-white flex items-center justify-between cursor-pointer hover:border-blue-500 transition-colors w-full ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() =>
                !isLoading && document.getElementById("file-upload")?.click()
              }
            >
              <span className="text-sm font-medium text-gray-700 truncate">
                Choose File
              </span>
              <span className="text-sm text-gray-500 ml-2 truncate">
                {file?.name || "No file chosen"}
              </span>
            </div>
          </div>

          <div className="bg-white/50 rounded-lg p-3 grid grid-cols-2 gap-x-8 gap-y-2 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-blue-600"></div>
              Format: JPG, PNG
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-blue-600"></div>
              Max size: 5MB
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-blue-600"></div>
              Square ratio preferred
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-blue-600"></div>
              Clear face visibility
            </div>
          </div>
        </div>

        {filePreview && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveFile();
            }}
            className="h-6 w-6 p-0 rounded-full bg-gray-100 hover:bg-gray-200 absolute top-4 right-4"
          >
            <X className="h-3 w-3 text-gray-500" />
          </Button>
        )}

        <input
          id="file-upload"
          type="file"
          className="hidden"
          disabled={isLoading}
          onChange={onFileChange}
          accept="image/png,image/jpeg,image/gif,image/webp"
        />
      </div>
    );
  },
);

// Helper: calculate hours difference between two "HH:MM" time strings
const calculateWorkingHours = (
  startTime: string | null,
  endTime: string | null,
): string => {
  if (!startTime || !endTime) return "0.0 hours";

  const [startH, startM] = startTime.split(":").map(Number);
  const [endH, endM] = endTime.split(":").map(Number);

  if (isNaN(startH) || isNaN(startM) || isNaN(endH) || isNaN(endM))
    return "0.0 hours";

  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;

  // Handle overnight shifts (end < start)
  const diffMinutes =
    endMinutes >= startMinutes
      ? endMinutes - startMinutes
      : 24 * 60 - startMinutes + endMinutes;

  const hours = diffMinutes / 60;
  return `${hours % 1 === 0 ? hours.toFixed(1) : hours.toFixed(2)} hours`;
};

const AvailabilitySchedule = ({ form }: { form: any }) => {
  const days = [
    { id: "monday", label: "Monday" },
    { id: "tuesday", label: "Tuesday" },
    { id: "wednesday", label: "Wednesday" },
    { id: "thursday", label: "Thursday" },
    { id: "friday", label: "Friday" },
    { id: "saturday", label: "Saturday" },
    { id: "sunday", label: "Sunday" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          Weekly Schedule
        </h3>
        <InfoTooltip text="Configure work schedule and availability for each day of the week" />
      </div>

      {days.map((day) => {
        const isActivePath = `availability_schedule.${day.id}.isActive`;
        const startTimePath = `availability_schedule.${day.id}.startTime`;
        const endTimePath = `availability_schedule.${day.id}.endTime`;
        const overtimePath = `availability_schedule.${day.id}.overtime`;

        const isActive = form.watch(isActivePath);
        const startTime = form.watch(startTimePath);
        const endTime = form.watch(endTimePath);
        const isOvertime = form.watch(overtimePath);

        // Dynamically calculate working hours
        const workingHoursDisplay = calculateWorkingHours(startTime, endTime);

        return (
          <div
            key={day.id}
            className={`border rounded-lg p-6 transition-colors ${isActive ? "bg-[#F4F7FF] border-[#BFDBFE]" : "bg-white border-gray-100"}`}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div
                  className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-sm ${isActive ? "bg-[#1D4ED8] text-white" : "bg-gray-100 text-gray-500"}`}
                >
                  {day.label.substring(0, 3)}
                </div>
                <div>
                  <span className="font-bold text-gray-900 block text-base">
                    {day.label}
                  </span>
                  <span className="text-xs text-gray-500 block font-medium">
                    {isActive ? "Working day" : "Day off"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  Available <InfoTooltip text="Toggle availability" />
                </span>
                <Switch
                  checked={isActive}
                  onCheckedChange={(checked) => {
                    form.setValue(isActivePath, checked);
                    // Clear times when marking as day off
                    if (!checked) {
                      form.setValue(startTimePath, null);
                      form.setValue(endTimePath, null);
                      form.setValue(overtimePath, false);
                    }
                  }}
                  className="data-[state=checked]:bg-blue-600"
                />
              </div>
            </div>
            {isActive && <Separator className="mb-4" />}
            {isActive && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                <div className="relative">
                  <label className="text-xs font-semibold text-gray-900 mb-1.5 flex items-center gap-1">
                    Shift Start Time <InfoTooltip text="Start of shift" />
                  </label>
                  <div className="relative">
                    <input
                      type="time"
                      className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                      {...form.register(startTimePath)}
                    />
                  </div>
                </div>
                <div className="relative">
                  <label className="text-xs font-semibold text-gray-900 mb-1.5 flex items-center gap-1">
                    Shift End Time <InfoTooltip text="End of shift" />
                  </label>
                  <div className="relative">
                    <input
                      type="time"
                      className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                      {...form.register(endTimePath)}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-900 mb-1.5 flex items-center gap-1">
                    Working Hours{" "}
                    <InfoTooltip text="Total hours calculated from start and end time" />
                  </label>
                  <div className="bg-gray-100 text-blue-600 border border-gray-200 h-10 px-3 flex items-center rounded-md text-sm font-semibold">
                    {workingHoursDisplay}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-900 mb-1.5 flex items-center gap-1">
                    Overtime <InfoTooltip text="Overtime allowed" />
                  </label>
                  <div className="flex items-center gap-3 h-10">
                    <Switch
                      checked={!!isOvertime}
                      onCheckedChange={(checked) =>
                        form.setValue(overtimePath, checked)
                      }
                      className="data-[state=checked]:bg-blue-600"
                    />
                    <span className="text-sm text-gray-700 font-medium whitespace-nowrap">
                      Available for overtime
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Helper for tooltip
const InfoTooltip = ({ text }: { text: string }) => (
  <div className="group relative flex items-center">
    <span className="text-gray-400 cursor-help hover:text-gray-600 transition-colors">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    </span>
    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10 text-center">
      {text}
      <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900"></div>
    </div>
  </div>
);

const formatAvailabilityScheduleForApi = (
  scheduleObj: Record<
    string,
    {
      isActive: boolean;
      startTime: string | null;
      endTime: string | null;
      overtime: boolean;
    }
  >,
) => {
  const dayOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  return dayOrder.map((dayKey) => {
    const entry = scheduleObj?.[dayKey]; // ✅ Now matches lowercase keys
    const isWeeklyOff = !entry?.isActive;

    return {
      day: dayKey.charAt(0).toUpperCase() + dayKey.slice(1), // Still sends "Monday" to API
      start_time: isWeeklyOff ? null : (entry?.startTime ?? null),
      end_time: isWeeklyOff ? null : (entry?.endTime ?? null),
      is_overtime: isWeeklyOff ? false : (entry?.overtime ?? false),
      is_weekly_off: isWeeklyOff,
    };
  });
};

const Edit = () => {
  const { id, ff_no } = useParams();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  // Hooks
  const { data: details, isLoading: isDetailsLoading } =
    useGetFieldForceDetails(ff_no || "");
  const { mutate: updateFieldForce } = useUpdateFieldForce(ff_no);
  const { mutateAsync } = useVerifyEmail();
  const { data: areasList } = useAreaList();
  const { data: skills } = useGetSkills();
  const { data: certificates } = useGetCertificates();
  const { data: payRates } = useRates();
  const skillsChoices = mapToLabelValue(skills, "skill", "id");
  const certificationChoices = mapToLabelValue(certificates, "name", "code");
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const areasChoices = mapToLabelValue(areasList, "name", "code");
  const [areaSelected, setAreaSelected] = useState(details?.area);
  const { data: subAreasList } = useSubAreaList(areaSelected);

  const handleVerifyEmail = async (email: string) => {
    if (!email) return;

    const resp = await mutateAsync({ email });
    return resp;
  };
  const subAreaChoices = mapToLabelValue(subAreasList, "name", "code");
  const normalPayOptions = React.useMemo(() => {
    return (
      payRates
        ?.filter((item) => item.rateType === 0)
        .map((item) => ({
          label: item.rate,
          value: item.rate,
        })) ?? []
    );
  }, [payRates]);

  const overtimePayOptions = React.useMemo(() => {
    return (
      payRates
        ?.filter((item) => item.rateType === 1)
        .map((item) => ({
          label: item.rate,
          value: item.rate,
        })) ?? []
    );
  }, [payRates]);
  // Define Fields for Profile Section
  const profileFields: FormField[] = useMemo(
    () => [
      {
        name: "file",
        type: "file",
        label: "",
        fullWidth: true,
        className: "col-span-3",
        customRender: () => (
          <FileUploadComponent
            file={file}
            filePreview={filePreview}
            isLoading={isSubmitting}
            onFileChange={handleFileChange}
            onRemoveFile={removeFile}
          />
        ),
      },
      {
        name: "",
        type: "custom",
        className: "col-span-3",
        customRender: () => {
          const hasMeterReaderRole =
            details?.roleCode?.includes("Meter Reader");

          if (!hasMeterReaderRole) return <></>;

          return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-3">
              <div className="bg-yellow-100 p-1.5 rounded-md mt-0.5">
                <AlertTriangle className="h-4 w-4 text-yellow-700" />
              </div>

              <div className="space-y-0.5">
                <p className="text-sm font-semibold text-yellow-900">
                  Multi-Role User
                </p>
                <p className="text-xs text-yellow-700">
                  This is a multi-role user with a{" "}
                  <span className="font-semibold">Meter Reader</span> role. Any
                  profile updates will also reflect in Field Force Management.
                </p>
              </div>
            </div>
          );
        },
      },
      {
        name: "fieldForceNo",
        type: "text",
        label: "Employee ID",
        placeholder: "Unique identifier",
        required: true,
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText: "Unique identifier assigned to the employee.",
      },
      {
        name: "name",
        type: "text",
        label: "Name",
        required: true,
        placeholder: "Full legal name",
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "Enter the employee's full legal name as it appears on official documents.",
      },
      {
        name: "email",
        type: "email",
        label: "Email",
        placeholder: "Primary email",
        showVerifyButton: true,
        onVerify: async (value, field) => {
          const resp = await handleVerifyEmail(value);

          if (!resp) return false;

          return !!resp.available;
        },
        required: false,
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "Primary email address for system notifications and important communications.",
      },
      {
        name: "contactNumber",
        type: "country-phone",
        label: "Phone",
        required: true,
        placeholder: "Contact number",
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "Mobile number with country code for direct contact and SMS notifications.",
      },
      {
        name: "department",
        type: "text",
        label: "Department",
        required: true,
        placeholder: "Department",
        disabled: true,
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "The organizational department this employee belongs to (e.g., Sales, Marketing).",
      },
      {
        name: "roleCode",
        type: "text",
        label: "Role",
        required: true,
        placeholder: "Role",
        disabled: true,
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "Employee's job title or position (e.g., Field Manager, Sales Executive).",
      },
      {
        name: "joiningDate",
        type: "datepicker",
        label: "Joining Date",
        placeholder: "Select date",
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "Date when the employee officially started working with the company.",
      },
      {
        name: "imeiNumber",
        type: "number",
        label: "IMEI Number",
        required: false,
        placeholder: "Device IMEI",
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "Device IMEI number used for tracking and managing the field force mobile device.",
      },
      {
        name: "address",
        type: "text",
        label: "Address",
        required: false,
        placeholder: "Address",
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "Complete residential address of the employee for communication and official purposes.",
      },

      // Password Header
      {
        name: "passwordHeader",
        type: "custom",
        label: "",
        fullWidth: true,
        className: "col-span-3 mt-6 pt-4 border-t border-gray-100",
        customRender: () => (
          <div className="w-full mb-2">
            <h3 className="text-base font-semibold text-gray-900">
              Password Management (Field Force App)
            </h3>
          </div>
        ),
      },
      {
        name: "password",
        type: "password",
        label: "Current Password",
        disabled: true,
        placeholder: "........",
        className: "col-span-1",
      },
      {
        name: "newPassword",
        type: "password",
        label: "New Password",
        required: false,
        placeholder: "Enter new password",
        minLength: 6,
        className: "col-span-1",
      },
      {
        name: "confirmPassword",
        type: "password",
        label: "Confirm New Password",
        required: false,
        placeholder: "Retype new password",
        className: "col-span-1",
      },
    ],
    [file, filePreview, isSubmitting],
  );

  // Define Fields for Other Details Section
  const otherFields: FormField[] = useMemo(
    () => [
      {
        name: "skills",
        type: "multi-select",
        options: skillsChoices,
        label: "Skills",
        placeholder: "List key competencies",
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "List employee's key competencies and abilities, separated by commas (e.g., Sales, Negotiation, CRM Software).",
      },
      {
        name: "certificates",
        type: "multi-select",
        options: certificationChoices,
        label: "Certifications",
        placeholder: "Professional certifications",
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "Professional certifications and credentials the employee has earned (e.g., Certified Sales Professional, PMP).",
      },
      {
        name: "area",
        type: "multi-select",
        options: areasChoices,
        label: "Area",
        placeholder: "Primary geographical assignment",
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "Primary geographical region where the employee operates (e.g., North Region, Downtown District).",
      },
      {
        name: "subArea",
        type: "multi-select",
        options: subAreaChoices,
        label: "Sub-area",
        placeholder: "Specific territory",
        className: "col-span-1",
        showHelperTooltip: true,
        tooltipText:
          "Specific territory or zone within the main area for detailed coverage management.",
      },
      {
        name: "normalPay",
        type: "select",
        options: normalPayOptions,
        label: `Normal Pay Rate (${currency}/hr)`,
        className: "col-span-1",
        helperText: "Standard hourly compensation rate",
        showHelperTooltip: true,
        tooltipText:
          "Standard hourly compensation rate for regular working hours. Used for salary calculations.",
      },
      {
        name: "overtimePay",
        type: "select",
        options: overtimePayOptions,
        label: `Overtime Pay Rate (${currency}/hr)`,
        className: "col-span-1",
        helperText: "Rate for hours beyond regular schedule",
        showHelperTooltip: true,
        tooltipText:
          "Hourly rate for hours worked beyond regular schedule, typically 1.5x normal rate.",
      },
    ],
    [
      areasChoices,
      subAreaChoices,
      skillsChoices,
      certificationChoices,
      overtimePayOptions,
      normalPayOptions,
    ],
  );

  const form = FormService.createForm({
    fields: [...profileFields, ...otherFields],
  });

  const mapAvailabilitySchedule = (
    schedule?: {
      day: string;
      startTime: string | null;
      endTime: string | null;
      isWeeklyOff: boolean;
      isOvertime: boolean;
    }[],
  ) => {
    if (!schedule?.length) return {};

    return schedule.reduce<Record<string, any>>((acc, item) => {
      const key = item.day.toLowerCase();

      acc[key] = {
        isActive: !item.isWeeklyOff,
        startTime: item.startTime ?? null,
        endTime: item.endTime ?? null,
        overtime: item.isOvertime ?? false,
      };

      return acc;
    }, {});
  };
  // Populating Form Data
  useEffect(() => {
    if (details) {
      const availabilitySchedule = mapAvailabilitySchedule(
        details.additionalData?.availabilitySchedule,
      );
      const formData = {
        ...details,
        currentPassword: details.password || "********",
        skills: details.skills || "",
        certificates: details?.additionalData?.certificates || "",
        area: details.area || "",
        subArea: details.subArea || "",
        normalPay: details?.additionalData?.normalPay,
        overtimePay: details?.additionalData?.overtimePay,
        department: details.extraDataMap?.department?.join(", "),
        roleCode: details.roleCode?.join(", "),
        address: details?.addressMap?.addressLine || "",
        availability_schedule: availabilitySchedule,
      };

      if (details.file) {
        setFilePreview(details.file);
      }

      FormService.populateForm(form, formData, [
        ...profileFields,
        ...otherFields,
      ]);
      if (formData.availability_schedule) {
        form.setValue("availability_schedule", formData.availability_schedule);
      }
    }
  }, [details, form]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (!selectedFile) return;

      const allowedTypes = ["image/png", "image/jpeg", "image/gif", "image/webp"];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast({
          title: "Invalid file type",
          description: "Only PNG, JPG, GIF, and WebP images are allowed.",
          variant: "destructive",
        });
        e.target.value = "";
        return;
      }

      setFile(selectedFile);
      form.setValue("file", selectedFile);
      const reader = new FileReader();
      reader.onload = (evt) => {
        setFilePreview(evt.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    },
    [form],
  );

  const removeFile = useCallback(() => {
    setFile(null);
    setFilePreview(null);
    form.setValue("file", null);
    const fileInput = document.getElementById(
      "file-upload",
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  }, [form]);

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      // Build the single unified payload matching the required API contract
      const dayOrder = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      const availabilityScheduleArray = dayOrder.map((dayKey) => {
        const entry = form.getValues(`availability_schedule.${dayKey}`);
        const isWeeklyOff = !entry?.isActive;
        return {
          day: dayKey.charAt(0).toUpperCase() + dayKey.slice(1),
          start_time: isWeeklyOff ? null : (entry?.startTime ?? null),
          end_time: isWeeklyOff ? null : (entry?.endTime ?? null),
          is_overtime: isWeeklyOff ? false : (entry?.overtime ?? false),
          is_weekly_off: isWeeklyOff,
        };
      });

      const payload: Record<string, any> = {
        field_force_no: data.fieldForceNo,
        name: data.name,
        email: data.email,
        contact_number: data.contactNumber ?? "0",
        imei_number: data.imeiNumber,
        joining_date: data.joiningDate ?? null,
        remote_utility_id: remoteUtilityId,
        address_meta: {
          address_line: data.address ?? "",
        },
        is_field_force: true,
        status: 0,
        additional_data: {
          normal_pay: parseFloat(data.normalPay ?? 0),
          overtime_pay: parseFloat(data.overtimePay ?? 0),
          certificates: data.certificates ?? [],
          availability_schedule: availabilityScheduleArray,
        },
        area: Array.isArray(data.area)
          ? data.area
          : data.area
            ? [data.area]
            : [],
        sub_area: Array.isArray(data.subArea)
          ? data.subArea
          : data.subArea
            ? [data.subArea]
            : [],
        skills: Array.isArray(data.skills)
          ? data.skills
          : data.skills
            ? [data.skills]
            : [],
        is_field_force_info: true,
      };

      // Only include password fields if a new password was provided
      if (data.newPassword) {
        if (data.newPassword !== data.confirmPassword) {
          toast({
            title: "Password and Confirm Password must match",
            variant: "destructive",
          });
          return; // stop execution
        }

        payload.password = data.newPassword;
        payload.confirm_password = data.confirmPassword;
      }

      // Build FormData for the main updateFieldForce call (supports file upload)
      const formData = new FormData();

      // Append scalar / nested fields as JSON string or individual keys
      // depending on what your API expects — adjust accordingly
      Object.entries(payload).forEach(([key, value]) => {
        if (key === "additional_data" || key === "address_meta") {
          formData.append(key, JSON.stringify(value));
        } else if (key === "area" || key === "sub_area" || key === "skills") {
          // Append each array item as a separate key
          (value as string[]).forEach((item: string) => {
            formData.append(key, item);
          });
        } else if (value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });

      if (data.file instanceof File) {
        formData.append("file", data.file);
      }

      // Fire all three update mutations in parallel
      updateFieldForce(formData, { onSuccess: () => navigate(-1) });
    } catch (error) {
      console.error(error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update profile",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isDetailsLoading) return <div>Loading...</div>;

  const handleFieldChange = (field: string, value: any) => {
    switch (field) {
      case "area":
        setAreaSelected(value);
    }
  };

  return (
    <div className=" space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4  mb-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5 text-gray-700" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Edit Profile</h1>
          <p className="text-sm text-gray-500 font-medium">
            {details?.employeeId || "Start updating details below"}
          </p>
        </div>
      </div>
      {/* Profile Details Card */}
      <Card className="p-6">
        {/* {details?.roleCode?.length > 1 && (
                    <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 flex items-start gap-3 rounded-t-lg">
                         <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                         <div>
                             <h3 className="text-yellow-800 font-semibold text-sm">Multi-Role User Profile</h3>
                             <p className="text-yellow-700 text-xs mt-0.5">This is a multi-role user with a Field Technician role. Any profile updates will also reflect in Field Force Management.</p>
                         </div>
                    </div>
                )} */}
        <h2 className="text-lg font-bold mb-6">Profile Details</h2>
        <DynamicForm
          form={form}
          fields={profileFields}
          onSubmit={() => {}}
          gridColumns={3}
          renderCustomFooter={() => <></>}
          config={{ classes: FIELD_CLASSES }}
        />
      </Card>

      {/* Other Details Card */}
      <Card className="p-6">
        <h2 className="text-lg font-bold mb-6">Other Details</h2>
        <DynamicForm
          form={form}
          fields={otherFields}
          onFieldChange={(field, value) => handleFieldChange(field, value)}
          onSubmit={() => {}}
          gridColumns={3}
          renderCustomFooter={() => <></>}
          config={{ classes: FIELD_CLASSES }}
        />
      </Card>

      {/* Availability Card */}
      <Card className="p-6">
        <h2 className="text-lg font-bold mb-6">Availability</h2>
        <AvailabilitySchedule form={form} />
      </Card>

      {/* Footer Actions */}
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={form.handleSubmit(onSubmit)}
          className="bg-blue-600 hover:bg-blue-700 w-32"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

export default Edit;
